// URL-driven i18n. Language is decided by the pathname:
//   /ar or /ar/... → lang="ar", dir="rtl"
//   everything else → lang="en", dir="ltr"
// The visitor's preference no longer lives in localStorage — the URL is the
// source of truth, which is what makes `/ar/*` share-able and SEO-crawlable.
import { createContext, useContext, useMemo, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { en as enCommon } from "@/i18n/en/common";
import { ar as arCommon } from "@/i18n/ar/common";
import * as enPages from "@/i18n/en/pages";
import * as arPages from "@/i18n/ar/pages";

function mergeWithFallback(base, over) {
  if (over == null) return base;
  if (typeof base !== "object" || typeof over !== "object" || Array.isArray(base)) return over ?? base;
  const out = {};
  const keys = new Set([...Object.keys(base || {}), ...Object.keys(over || {})]);
  for (const k of keys) out[k] = mergeWithFallback(base?.[k], over?.[k]);
  return out;
}

/** Returns { lang, otherLang, arPath, enPath } for a given pathname. */
export function computeLangFromPath(pathname) {
  const isAr = pathname === "/ar" || pathname.startsWith("/ar/");
  if (isAr) {
    const stripped = pathname === "/ar" ? "/" : pathname.slice(3);
    return { lang: "ar", otherLang: "en", arPath: pathname, enPath: stripped || "/" };
  }
  return { lang: "en", otherLang: "ar", arPath: "/ar" + (pathname === "/" ? "" : pathname), enPath: pathname };
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, arPath, enPath } = useMemo(() => computeLangFromPath(location.pathname), [location.pathname]);
  const dir = lang === "ar" ? "rtl" : "ltr";

  const toggleLang = useCallback(() => {
    navigate(lang === "ar" ? enPath : arPath);
  }, [lang, arPath, enPath, navigate]);

  const setLang = useCallback((l) => {
    navigate(l === "ar" ? arPath : enPath);
  }, [arPath, enPath, navigate]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
  }, [dir, lang]);

  const t = useMemo(() => (lang === "ar" ? mergeWithFallback(enCommon, arCommon) : enCommon), [lang]);

  const tr = useCallback((path, fallback) => {
    if (lang !== "ar") return fallback;
    const [ns, ...rest] = path.split(".");
    let node = arPages?.[ns];
    for (const seg of rest) {
      if (node == null) break;
      node = node[seg];
    }
    if (node == null || node === "") return fallback;
    return node;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, dir, t, tr, toggleLang, setLang, arPath, enPath }),
    [lang, dir, t, tr, toggleLang, setLang, arPath, enPath],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
